import type { CountSpecMisc, WidgetStartDateType } from "../../Processing/types/builderMisc";
import { v4 as uuidv4 } from "uuid";
import type {
  GaugeComponentSpec,
  GaugeBuilderMisc,
  GaugeScaleConfig,
  GaugeSegmentConfig,
  GaugeScaleType,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import { resolveSubjectNames } from "../shared/PlayersForm";

export interface GaugeFormState {
  /** UUID of the Slicer this section follows, when it is bound. */
  dateControllerId?: string;
  subjectControllerId?: string;
  subjectControllerMode?: "positions";
  queryId: string;
  scaleQueryId: string;
  title: string;
  /** Form-only: true once the user manually edits the title, which stops it auto-following the metric. */
  titleDirty: boolean;
  selectedMetricId: string | null;
  /** Which items a count metric tallies. Absent for every measured metric. */
  countSpec?: CountSpecMisc;
  metricAgg: "avg" | "sum" | "best";
  metricIdentifierType: "metric" | "acwr" | "count";
  searchQuery: string;
  activeCategory: string;
  dateRange: string;
  showAdvancedDate: boolean;
  startDateType: WidgetStartDateType;
  startDateLatestN?: number;
  startDateAbsolute: string;
  startDateOffset: number;
  endDateType: "relative" | "absolute";
  endDateAbsolute: string;
  endDateOffset: number;
  dayFilter: string[];
  selectedSubjects: string[];
  selectedDrills: string[];
  subjectActiveTab: "athletes" | "drills";
  selectedSubjectNames: string[];
  excludeInjuredPlayers: boolean;
  scale: GaugeScaleConfig;
  segmentConfig: GaugeSegmentConfig;
}

export function createInitialState(): GaugeFormState {
  return {
    queryId: uuidv4(),
    scaleQueryId: uuidv4(),
    title: "",
    titleDirty: false,
    selectedMetricId: null,
    countSpec: undefined,
    metricAgg: "avg",
    metricIdentifierType: "metric",
    searchQuery: "",
    activeCategory: "GPS",
    dateRange: "last4weeks",
    showAdvancedDate: false,
    startDateType: "relative",
    startDateAbsolute: "",
    startDateOffset: 28,
    endDateType: "relative",
    endDateAbsolute: "",
    endDateOffset: 0,
    dayFilter: [],
    selectedSubjects: ["full-team"],
    selectedDrills: [],
    subjectActiveTab: "athletes",
    selectedSubjectNames: [],
    excludeInjuredPlayers: true,
    scale: {
      type: "team_best",
      multiplier: 1.0,
      customMax: null,
      min: 0,
    },
    segmentConfig: {
      segments: 3,
      colors: [...HIGH_GOOD_SEGMENT_COLORS],
      thresholds: [0.33, 0.66],
    },
  };
}

// The default gauge palette runs low -> high, so red at the bottom reads "more is
// better". A count of flags, injuries or treatments is the other way round: fewer is
// better, so its palette runs green -> red.
//
// A status count is deliberately absent. A status can be a good thing to have
// (available) or a bad one (injured), so its direction is the author's call, not
// something the source can decide.
export const HIGH_GOOD_SEGMENT_COLORS = ["#FF4D4F", "#FFA940", "#52C41A"];
export const LOW_GOOD_SEGMENT_COLORS = ["#52C41A", "#FFA940", "#FF4D4F"];

const LOW_GOOD_COUNT_SOURCES = new Set(["flags", "injuries", "treatments"]);

/**
 * The segment config for a newly chosen metric.
 *
 * Only a palette still at one of the two defaults is swapped. A palette the author
 * picked, or one with a different number of segments, is left exactly as it is.
 */
export function segmentConfigForSource(
  config: GaugeSegmentConfig,
  source: string | null,
): GaugeSegmentConfig {
  const wanted = source && LOW_GOOD_COUNT_SOURCES.has(source)
    ? LOW_GOOD_SEGMENT_COLORS
    : HIGH_GOOD_SEGMENT_COLORS;
  const isDefault = (palette: string[]) =>
    config.colors.length === palette.length
    && config.colors.every((c, i) => c.toUpperCase() === palette[i].toUpperCase());
  if (!isDefault(HIGH_GOOD_SEGMENT_COLORS) && !isDefault(LOW_GOOD_SEGMENT_COLORS)) {
    return config;
  }
  return { ...config, colors: [...wanted] };
}

function buildScaleSubjectFilter(
  scaleType: GaugeScaleType,
  state: GaugeFormState,
  getSubjectEntities: (t: string) => SubjectEntity[],
): string[] {
  const needleSubjects =
    state.selectedSubjects.length > 0 || state.selectedDrills.length > 0
      ? [...state.selectedSubjects, ...state.selectedDrills]
      : ["full-team"];

  switch (scaleType) {
    case "player_avg":
    case "player_best":
    case "player_sum":
      return needleSubjects;

    case "position_avg":
    case "position_best":
    case "position_sum": {
      // Subject filters must be prefixed (schema: ^(full-team|(drill|player|pos|group)-\d+)$).
      // Prefer a directly-selected position (already prefixed, e.g. "pos-4").
      const selectedPos = state.selectedSubjects.find((s) => s.startsWith("pos-"));
      if (selectedPos) return [selectedPos];

      const playerSubject = state.selectedSubjects.find((s) => s.startsWith("player-"));
      if (!playerSubject) return ["full-team"];
      const playerId = playerSubject.slice("player-".length);
      const playerEntities = getSubjectEntities("player");
      const playerEntity = playerEntities.find((e) => e.id === playerId);
      const positionName = playerEntity?.detail;
      if (!positionName) return ["full-team"];
      const posEntities = getSubjectEntities("position");
      const posEntity = posEntities.find((e) => e.name === positionName);
      return posEntity ? [`pos-${posEntity.id}`] : ["full-team"];
    }

    case "team_avg":
    case "team_best":
    case "team_sum":
      return ["full-team"];

    default:
      return [];
  }
}

export function formStateToSpec(
  state: GaugeFormState,
  _metrics: MetricOption[],
  getSubjectEntities?: (t: string) => SubjectEntity[],
): GaugeComponentSpec | null {
  if (!state.selectedMetricId) return null;

  // Custom is the only scale type with a concrete max at config time; it must exceed the min.
  if (
    state.scale.type === "custom" &&
    (state.scale.customMax == null || state.scale.min >= state.scale.customMax)
  ) {
    return null;
  }

  // Threshold stops must be strictly increasing so segments don't overlap or invert.
  const { thresholds } = state.segmentConfig;
  if (thresholds.some((t, i) => i > 0 && t <= thresholds[i - 1])) {
    return null;
  }

  const metric = `${state.selectedMetricId}-${state.metricAgg}`;
  const needsScaleQuery = !["custom", "baseline_multiplier"].includes(state.scale.type);
  const queryId = state.queryId;
  const scaleQueryId = needsScaleQuery ? state.scaleQueryId : null;

  const scaleSubjectFilter =
    needsScaleQuery && getSubjectEntities
      ? buildScaleSubjectFilter(state.scale.type as GaugeScaleType, state, getSubjectEntities)
      : [];

  const selectedSubjectNames = getSubjectEntities
    ? resolveSubjectNames(state.selectedSubjects, state.selectedDrills, getSubjectEntities)
    : state.selectedSubjectNames;

  const misc: GaugeBuilderMisc = {
    selectedMetricId: state.selectedMetricId,
    countSpec: state.countSpec,
    metricAgg: state.metricAgg,
    metricIdentifierType: state.metricIdentifierType,
    searchQuery: state.searchQuery,
    activeCategory: state.activeCategory,
    dateRange: state.dateRange,
    showAdvancedDate: state.showAdvancedDate,
    startDateType: state.startDateType,
    startDateLatestN: state.startDateLatestN,
    startDateAbsolute: state.startDateAbsolute,
    startDateOffset: state.startDateOffset,
    endDateType: state.endDateType,
    endDateAbsolute: state.endDateAbsolute,
    endDateOffset: state.endDateOffset,
    dayFilter: state.dayFilter,
    dateControllerId: state.dateControllerId,
    subjectControllerId: state.subjectControllerId,
    subjectControllerMode: state.subjectControllerMode,
    selectedSubjects: state.selectedSubjects,
    selectedDrills: state.selectedDrills,
    subjectActiveTab: state.subjectActiveTab,
    selectedSubjectNames,
    excludeInjuredPlayers: state.excludeInjuredPlayers,
    scaleSubjectFilter,
  };

  return {
    type: "gauge",
    display: { title: state.title },
    queryId,
    scaleQueryId,
    metric,
    metricIdentifierType: state.metricIdentifierType,
    scale: state.scale,
    scale_color_mode: "fixed_segments",
    segment_config: state.segmentConfig,
    gauge_builder_misc: misc,
  };
}

export function hydrateFromSpec(
  spec: GaugeComponentSpec,
): GaugeFormState {
  const misc = spec.gauge_builder_misc;
  const metricParts = spec.metric ? spec.metric.split(/-(avg|sum|best)$/).filter(Boolean) : [null, "avg"];
  const rawMetricId = metricParts[0] as string | null;
  const agg = metricParts[1] ?? "avg";

  return {
    queryId: spec.queryId,
    scaleQueryId: spec.scaleQueryId ?? uuidv4(),
    title: spec.display.title ?? "",
    // Existing widgets keep their saved title — don't let it auto-follow the metric.
    titleDirty: true,
    selectedMetricId: (misc.selectedMetricId ?? rawMetricId) as string | null,
    countSpec: misc.countSpec,
    metricAgg: (misc.metricAgg ?? agg) as "avg" | "sum" | "best",
    metricIdentifierType: (misc.metricIdentifierType ?? "metric") as "metric" | "acwr",
    searchQuery: misc.searchQuery ?? "",
    activeCategory: misc.activeCategory ?? "GPS",
    dateRange: misc.dateRange ?? "last4weeks",
    showAdvancedDate: misc.showAdvancedDate ?? false,
    startDateType: misc.startDateType ?? "relative",
    startDateLatestN: misc.startDateLatestN,
    startDateAbsolute: misc.startDateAbsolute ?? "",
    startDateOffset: misc.startDateOffset ?? 28,
    endDateType: (misc.endDateType ?? "relative") as "relative" | "absolute",
    endDateAbsolute: misc.endDateAbsolute ?? "",
    endDateOffset: misc.endDateOffset ?? 0,
    dayFilter: misc.dayFilter ?? [],
    dateControllerId: misc.dateControllerId,
    subjectControllerId: misc.subjectControllerId,
    subjectControllerMode: misc.subjectControllerMode,
    selectedSubjects: misc.selectedSubjects ?? [],
    selectedDrills: misc.selectedDrills ?? [],
    subjectActiveTab: (misc.subjectActiveTab ?? "athletes") as "athletes" | "drills",
    selectedSubjectNames: misc.selectedSubjectNames ?? [],
    excludeInjuredPlayers: misc.excludeInjuredPlayers ?? true,
    scale: spec.scale,
    segmentConfig: spec.segment_config as GaugeSegmentConfig,
  };
}
